// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import iconsObject from "@eisinc/XDL/Icons/eisCommonIcons";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import mobiusIconsObject from "@insite/mobius/Icons/commonIcons";
import Toaster from "@insite/mobius/Toast/Toaster";
import ToasterContext, { useToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

import { SectionContainer, SectionTitle } from "@eisinc/App/Widgets/DesignSystem/DesignSystem.helpers";
import { clipboardText } from "@eisinc/App/Widgets/DesignSystem/Utilities";
import capitalize from "lodash/capitalize";
import React, { FC } from "react";
import { css } from "styled-components";

const IconsItem = ({ iconSrc, name, styles }: { iconSrc: any; name: string; styles: IconItemStyles }) => {
    const toaster = useToaster();
    return (
        <Clickable
            onClick={() => {
                const isCopied = clipboardText(name);
                toaster.addToast({
                    body: isCopied ? (
                        <Typography>
                            <strong>{name}</strong> icon is copied
                        </Typography>
                    ) : (
                        <Typography>
                            copy <strong>{name}</strong> icon failed
                        </Typography>
                    ),
                    messageType: isCopied ? "success" : "danger",
                });
            }}
            aria-label="Icons Tab"
        >
            <StyledWrapper {...styles.container}>
                <Icon src={iconSrc} {...styles.icon} />
                <Typography {...styles.title}>{name}</Typography>
            </StyledWrapper>
        </Clickable>
    );
};

interface IconItemStyles {
    container?: InjectableCss;
    icon?: IconPresentationProps;
    title?: TypographyPresentationProps;
}

interface IconsTabStyles {
    titleGridItem?: GridItemProps;
    gridItem?: GridItemProps;
    pathClickable?: ClickableProps;
    pathTypography?: TypographyPresentationProps;
    iconsContainer?: InjectableCss;
    iconItem: IconItemStyles;
}

const iconsTabStyles: IconsTabStyles = {
    titleGridItem: {
        width: 12,
    },
    gridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            flex-wrap: wrap;
        `,
    },
    pathClickable: {
        css: css`
            width: auto;
            &:focus {
                outline: none;
            }
        `,
    },
    pathTypography: {
        size: fontConfig.small.size,
        css: css`
            margin-left: 0;
            margin-bottom: 15px;
            background: ${getColor("common.ui2")};
            color: ${getColor("secondary.main")};
            line-height: ${fontConfig.small.lineHeight};
            max-width: 100%;
            overflow: auto;
            padding: 15px 22px;
            display: block;
            word-wrap: break-word;
            user-select: none;
            border: 1px solid ${getColor("common.gray")};
            border-left: 3px solid ${getColor("warning.main")};
        `,
    },
    iconsContainer: {
        css: css`
            display: flex;
            gap: 50px;
            flex-wrap: wrap;
            justify-content: flex-start;
            padding: 15px 0;
        `,
    },
    iconItem: {
        container: {
            css: css`
                overflow-wrap: break-word;
                width: 135px;
                display: flex;
                flex-direction: column;

                border-radius: 8px;
                box-shadow: rgb(0 58 99 / 15%) 0 0 9px 0;
                transition: box-shadow ease-in 150ms;

                & > div {
                    border-top-left-radius: 8px;
                    border-top-right-radius: 8px;
                    border-bottom: 1px solid ${getColor("common.ui3")};
                }
                &:hover {
                    box-shadow: rgb(0 58 99 / 30%) 0 0 15px 0;
                }
            `,
        },
        icon: {
            css: css`
                margin-top: 15px;
                justify-content: center;
                &,
                & svg {
                    height: 24px;
                    width: initial;
                }
            `,
        },
        title: {
            size: fontConfig.xSmall.size,
            css: css`
                color: ${getColor("text.main")};
                display: block;
                margin-top: 20px;
                padding: 0 8px;
                margin-bottom: 15px;
                text-align: center;
            `,
        },
    },
};

const styles = iconsTabStyles;
const IconsTab: FC = props => {
    const blueprintsIcons = [
        {
            path: "@eis/XDL/Icons",
            icons: iconsObject,
            name: "EIS",
        },
        {
            path: "@insite/mobius/Icons",
            icons: mobiusIconsObject,
            name: "mobius",
        },
    ];

    return (
        <SectionContainer>
            <Toaster position="bottom" mobilePosition="bottom">
                <ToasterContext.Consumer>
                    {({ addToast }) => (
                        <>
                            {blueprintsIcons.map((blueprintIcons, index: number) => {
                                if (Object.keys(blueprintIcons.icons).length === 0) {
                                    return null;
                                }

                                return (
                                    <React.Fragment key={index}>
                                        <GridItem {...styles.titleGridItem}>
                                            <SectionTitle>{`${capitalize(blueprintIcons.name)} Icons`}</SectionTitle>
                                        </GridItem>
                                        <GridItem {...styles.gridItem} key={blueprintIcons.path}>
                                            <Clickable
                                                {...styles.pathClickable}
                                                onClick={() => {
                                                    const isCopied = clipboardText(blueprintIcons.path);
                                                    addToast({
                                                        body: isCopied
                                                            ? `Import path is copied`
                                                            : `Copy Import path failed.`,
                                                        messageType: isCopied ? "success" : "danger",
                                                    });
                                                }}
                                                aria-label="Icons Tab"
                                            >
                                                <Typography {...styles.pathTypography}>
                                                    {blueprintIcons.path}
                                                </Typography>
                                            </Clickable>
                                            <StyledWrapper {...styles.iconsContainer}>
                                                {Object.keys(blueprintIcons.icons).map(iconName => (
                                                    <IconsItem
                                                        key={iconName}
                                                        iconSrc={blueprintIcons.icons[iconName]}
                                                        name={iconName}
                                                        styles={styles.iconItem}
                                                    />
                                                ))}
                                            </StyledWrapper>
                                        </GridItem>
                                    </React.Fragment>
                                );
                            })}
                        </>
                    )}
                </ToasterContext.Consumer>
            </Toaster>
        </SectionContainer>
    );
};

export default IconsTab;
